import type { OptionDB } from "@project/emerge";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import type {
  RollerBoardInfos,
  RollerBoardLayoutMode,
} from "../interfaces/RollerBoardInfos";
import type {
  CreeperRollerType,
  RollerType,
} from "../interfaces/RollerBoardProperties";
import { OptionDBInstance } from "../user_options/options_db";
import { createVariableOption } from "./createVariableOption";

const asCreeperRoller = (roller: RollerType | undefined): CreeperRollerType => {
  if (!roller || roller.rollerType !== ROLLER_TYPE.CREEPER) {
    throw new Error("Expected a CREEPER roller type for roller board option.");
  }
  return roller as CreeperRollerType;
};

export const createRollerBoard = (
  optionDb: OptionDB = OptionDBInstance,
  rollerA?: CreeperRollerType,
  rollerB?: CreeperRollerType,
  layoutMode: RollerBoardLayoutMode = "AUTO",
): RollerBoardInfos => {
  // Default value for roller distance from edge (belongs to the roller board)
  const defaultRollerDistanceFromEdge = 27;

  // If rollers are provided, use them
  if (rollerA && rollerB) {
    return createVariableOption<RollerBoardInfos>(
      VariableOptionType.ROLLER_BOARD,
      {
        layoutMode,
        rollerA: asCreeperRoller(rollerA),
        rollerB: asCreeperRoller(rollerB),
        splitThresholdMm: 650,
        singlePanelDepthInsetMm: 12,
        singlePanelSideInsetMm: 62,
        rollerDistanceFromEdge: defaultRollerDistanceFromEdge,
      },
    );
  }

  // Default: find first CREEPER roller from database and use it for both rollers
  const allRollers = optionDb.getAllOfKind<RollerType>(
    LTCaseUserOption.RollerType,
  );
  const creeperRollers = allRollers.filter(
    (r) => r.rollerType === ROLLER_TYPE.CREEPER,
  ) as CreeperRollerType[];

  if (creeperRollers.length === 0) {
    throw new Error(
      "No CREEPER rollers found in database. Please import roller options first.",
    );
  }

  // Use the first roller for both rollerA and rollerB by default
  const defaultRoller = creeperRollers[0];
  if (!defaultRoller) {
    throw new Error(
      "No CREEPER rollers found in database. Please import roller options first.",
    );
  }

  return createVariableOption<RollerBoardInfos>(
    VariableOptionType.ROLLER_BOARD,
    {
      layoutMode,
      rollerA: defaultRoller,
      rollerB: defaultRoller, // Use same roller for both by default
      splitThresholdMm: 650,
      singlePanelDepthInsetMm: 12,
      singlePanelSideInsetMm: 62,
      rollerDistanceFromEdge: defaultRollerDistanceFromEdge,
    },
  );
};
